import axios from 'axios';
import { authService } from '@/services/auth/authService';
import i18n from '@/i18n';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5050';

/**
 * Response shape from ABP's /api/abp/application-configuration endpoint.
 * We use this instead of /api/abp/application-localization because the
 * configuration endpoint reliably returns ALL module resources.
 */
interface AbpConfigurationResponse {
  localization?: {
    values?: Record<string, Record<string, string>>;
  };
}

/**
 * Fetch ABP localization texts for a given culture and merge them into i18next.
 *
 * Uses the /api/abp/application-configuration endpoint with Accept-Language
 * header to get all localization resources for the specified culture.
 * The fetched texts are merged into i18next's default 'translation' namespace
 * so components use a single t() function for both bundled UI strings and
 * server-provided domain labels.
 */
export async function loadAbpLocalization(culture: string): Promise<void> {
  try {
    const token = authService.getToken();
    const headers: Record<string, string> = {
      'Accept-Language': culture,
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const tenantId = authService.getTenantId();
    if (tenantId) {
      headers['__tenant'] = tenantId;
    }

    const response = await axios.get<AbpConfigurationResponse>(
      `${API_BASE_URL}/api/abp/application-configuration`,
      { headers },
    );

    // Flatten all resource texts into a single map
    const texts: Record<string, string> = {};
    const values = response.data?.localization?.values;
    if (values) {
      for (const resourceTexts of Object.values(values)) {
        if (resourceTexts) {
          Object.assign(texts, resourceTexts);
        }
      }
    }

    // Merge into i18next's default namespace
    i18n.addResourceBundle(culture, 'translation', texts, true, true);

    // Force all useTranslation() hooks to re-render with the new texts
    i18n.changeLanguage(culture);
  } catch (error) {
    console.warn(`Failed to load ABP localization for culture "${culture}":`, error);
  }
}
